import React, { useEffect, useState } from "react";
import { Container, Alert, ModalDialog } from "../../../components";
import { Heading, Text } from "@telia/teddy";
import _isEmpty from "lodash/isEmpty";
import { connect, useDispatch } from "react-redux";
import PAGES from "../../../../../constants/pages";
import { RootState } from "../../../../../models";
import { EmployeeInfoFields } from "../../../../../models/state";
import {
    isBankIdVerified as isBankIdVerifiedSelector,
    isBankIdAvailable as isBankIdAvailableSelector,
    isBankIdMandatory as isBankIdMandatorySelector,
    isBankIdPerformed as isBankIdPerformedSelector,
} from "../../../authentication/state/selectors";
import AUTHENTICATION_TEXT from "../../../authentication/text-consts";
import BankIdSection from "../../../checkout/components/molecules/BankIdSectionTeliaBroker/BankIdSectionTeliaBroker";
import ContactCard from "../../../checkout/components/molecules/ContactCard/ContactCard";
import DsfMismatchForm from "../../../checkout/components/organisms/DsfMismatchForm/DsfMismatchForm";
import DsfMismatchFormContinueButton from "../../../checkout/components/organisms/PersonalDetailsContinueButton/DsfMismatchFormContinueButton";
import { resetOrder } from "../../../checkout/state/actions/phoneNumberActions";
import "./PersonalDetails.less";
import { isTypeLoading } from "../../../loading/state/selectors";
import EmailForm from "../../components/organisms/EmailForm/EmailForm";
import { addEmail, verifyUpdatedName } from "../../state/actions/onboardingActions";
import { ONBOARDING_VERIFY_UPDATED_NAME } from "../../state/actionTypes";
import {
    getEmployeeInfo,
    isOrderFound as getIsOrderFound,
    isOrderSubmitable as getIsOrderSubmitable,
    getLegalOwnerOrgName,
    isSubmitEmailSuccess,
} from "../../state/selectors";
import TEXT from "../../text-consts";

interface StateProps {
    employeeInfo: EmployeeInfoFields | undefined;
    isBankIdAvailable: boolean;
    isBankIdMandatory: boolean;
    isBankIdPerformed: boolean;
    isBankIdVerified: boolean;
    isOrderFound: boolean;
    isOrderSubmitable: boolean;
    isSubmittingDsfMismatchForm: boolean;
    newLegalOwnerOrgName?: string;
    submitEmailSuccess: boolean;
}

type PersonalDetailsProps = StateProps;

const PersonalDetails: React.FC<PersonalDetailsProps> = ({
    // Redux (data)
    employeeInfo,
    isBankIdAvailable,
    isBankIdMandatory,
    isBankIdPerformed,
    isBankIdVerified,
    isOrderFound,
    isOrderSubmitable,
    newLegalOwnerOrgName,
    submitEmailSuccess,
    isSubmittingDsfMismatchForm,
}) => {
    const dispatch = useDispatch();
    const [showLogOutBankIdModal, setShowLogoutBankIdModal] = useState(false);
    const LoadingAlert = ({ message }: { message: string }) => (
        <Container className="beta-checkout--center" size="small" noMargin noPadding>
            <Alert kind="warning">{message}</Alert>
        </Container>
    );

    useEffect(() => {
        if (submitEmailSuccess) {
            window.location.assign(PAGES.ONBOARDING_B2B.SUBSCRIPTION_DETAILS.URL);
        }
    }, [submitEmailSuccess]);

    function shouldShowBankIdNotAvailableWhenMandatory() {
        return !isBankIdAvailable && isBankIdMandatory;
    }

    function renderLoadingAlerts() {
        if (!isOrderSubmitable) {
            return <LoadingAlert message={TEXT.ONBOARDING_B2B.ORDER_NOT_CORRECT_STATE} />;
        } else if (!isOrderFound) {
            return <LoadingAlert message={TEXT.ONBOARDING_B2B.MISSING_ORDER_TEXT} />;
        }
        return null;
    }

    function renderBankIdAlerts() {
        if (shouldShowBankIdNotAvailableWhenMandatory()) {
            return (
                <Container className="beta-checkout--center" size="small" noMargin noPadding>
                    <Alert kind="warning">BankID er ikke tilgjengelig akkurat nå.</Alert>
                </Container>
            );
        }
        return null;
    }

    const bankIdAlert = renderBankIdAlerts();
    const loadingAlerts = renderLoadingAlerts();

    useEffect(() => {
        if (isBankIdVerified && isBankIdPerformed && _isEmpty(employeeInfo)) {
            window.location.assign(`${PAGES.ONBOARDING_B2B.CONTACT_INFORMATION.URL}`);
        }
    }, [isBankIdVerified, isBankIdPerformed]);

    return isOrderFound && isOrderSubmitable ? (
        <div className="onboarding-b2b">
            {!isBankIdPerformed && (
                <>
                    <div className="onboarding-b2b__heading-container">
                        <Container size="medium" noMargin>
                            <Heading mb="100" className="onboarding-b2b__heading" as="h2" variant="title-300">
                                {TEXT.ONBOARDING_B2B.HEADING}
                            </Heading>
                            <Text as="p" data-di-mask>
                                {TEXT.ONBOARDING_B2B.INGRESS(newLegalOwnerOrgName || "Din nye arbeidsgiver")}
                            </Text>
                            <div className="onboarding-b2b__bankid-wrapper">
                                <BankIdSection
                                    referer="onboardingB2B"
                                    skipDisplayHeading={true}
                                    skipDisplayDescription={true}
                                />
                                <Text variant="additional-100">
                                    {AUTHENTICATION_TEXT.BANK_ID.DESCRIPTION_ONBOARDING_B2B}
                                </Text>
                                {bankIdAlert}
                                {loadingAlerts}
                            </div>
                        </Container>
                    </div>
                </>
            )}
            {isBankIdPerformed && !isBankIdVerified && (
                <Container size="medium">
                    <Heading mb="100" className="onboarding-b2b__heading" as="h2" variant="title-300">
                        {TEXT.ONBOARDING_B2B.HEADING}
                    </Heading>
                    <DsfMismatchForm
                        update={async (firstName, lastName) => dispatch(verifyUpdatedName(firstName, lastName))}
                        setShowLogoutBankIdModal={(show) => setShowLogoutBankIdModal(show)}
                    />
                    <DsfMismatchFormContinueButton isSubmitting={isSubmittingDsfMismatchForm} />
                </Container>
            )}
            {isBankIdPerformed && isBankIdVerified && !_isEmpty(employeeInfo) && (
                <>
                    <Heading mb="100" className="onboarding-b2b__heading" as="h1" variant="title-300">
                        {TEXT.ONBOARDING_B2B.CONTACT_INFORMATION.HEADING}
                    </Heading>
                    <ContactCard
                        headingText={TEXT.ONBOARDING_B2B.CONTACT_INFORMATION.INGRESS}
                        name={employeeInfo.firstName + " " + employeeInfo.lastName}
                        address={employeeInfo.address?.street}
                        city={employeeInfo.address.postcode + " " + employeeInfo.address?.city}
                        resetBankId={() => setShowLogoutBankIdModal(true)}
                        showContactInfo={false}
                        showLogoutBankId={true}
                    />
                    <Container className="onboarding-b2b__email-container" size="medium">
                        <EmailForm onSubmitDetails={() => dispatch(addEmail())} />
                    </Container>
                </>
            )}
            {showLogOutBankIdModal && (
                <ModalDialog
                    className="contact-card-container__logout-bankid"
                    size="small"
                    submitText="Ja, logg meg ut"
                    closeText="Nei, bli på siden"
                    closeKind="secondary"
                    heading="Ønsker du å logge ut av BankID?"
                    name="log-out-bank-id-modal"
                    onSubmit={() => {
                        dispatch(resetOrder());
                        setShowLogoutBankIdModal(false);
                    }}
                    onClose={() => setShowLogoutBankIdModal(false)}
                />
            )}
        </div>
    ) : (
        <>{loadingAlerts}</>
    );
};

const mapStateToProps = (state: RootState): StateProps => ({
    isBankIdVerified: isBankIdVerifiedSelector(state),
    isOrderFound: getIsOrderFound(state),
    isOrderSubmitable: getIsOrderSubmitable(state),
    isBankIdPerformed: isBankIdPerformedSelector(state),
    isBankIdAvailable: isBankIdAvailableSelector(state),
    isBankIdMandatory: isBankIdMandatorySelector(state),
    employeeInfo: getEmployeeInfo(state),
    submitEmailSuccess: isSubmitEmailSuccess(state) || false,
    isSubmittingDsfMismatchForm: isTypeLoading(state, ONBOARDING_VERIFY_UPDATED_NAME),
    newLegalOwnerOrgName: getLegalOwnerOrgName(state),
});

export default connect(mapStateToProps)(PersonalDetails);
